import { useProjectStore } from "src/stores/projectStore";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { parseAgenticChatStoryBibleLink } from "../chatMarkdownLinkUri";
import { markEntityNavigationInProgress } from "./entityNavigationState";
import { getEntityNavigationPlan } from "./entityNavigationPlan";

/** LeftBar listens for this to expand the Story Bible section when it is collapsed (SW2). */
export const EXPAND_STORY_BIBLE_MENU_EVENT = "sudowrite:expand-story-bible-menu";
export const EXPAND_STORY_BIBLE_ENTITY_EVENT = "sudowrite:expand-story-bible-entity";
export type NavigateToEntityMentionOptions = {
  /** Active document in the current editor/chat context. Falls back to project store document id. */
  activeDocumentId?: string | null;
};

export function getStoryBibleEntityExpandRequestId(event: Event): string | null {
  if (!(event instanceof CustomEvent)) {
    return null;
  }

  const requestId = event.detail?.id;
  return typeof requestId === "string" ? requestId : null;
}

const ENTITY_SCROLL_PADDING_PX = 12;

function isScrollableElement(element: HTMLElement): boolean {
  if (element === document.body || element === document.documentElement) {
    return false;
  }

  const style = window.getComputedStyle(element);
  const overflowY = style.overflowY || style.overflow;
  return /(auto|scroll|overlay)/.test(overflowY) && element.scrollHeight > element.clientHeight;
}

function getNearestScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let parent = element.parentElement;
  while (parent) {
    if (isScrollableElement(parent)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

function scrollElementWithinContainer(element: HTMLElement, container: HTMLElement, block: ScrollLogicalPosition): void {
  const elementRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const topOffset = elementRect.top - containerRect.top;
  const bottomOffset = elementRect.bottom - containerRect.bottom;

  if (block === "nearest") {
    if (topOffset < ENTITY_SCROLL_PADDING_PX) {
      container.scrollTop += topOffset - ENTITY_SCROLL_PADDING_PX;
    } else if (bottomOffset > -ENTITY_SCROLL_PADDING_PX) {
      container.scrollTop += bottomOffset + ENTITY_SCROLL_PADDING_PX;
    }
    return;
  }

  if (block === "center") {
    const elementCenter = elementRect.top + elementRect.height / 2;
    const containerCenter = containerRect.top + containerRect.height / 2;
    container.scrollTop += elementCenter - containerCenter;
    return;
  }

  if (block === "end") {
    container.scrollTop += bottomOffset + ENTITY_SCROLL_PADDING_PX;
    return;
  }

  container.scrollTop += topOffset - ENTITY_SCROLL_PADDING_PX;
}

function scrollToEntityTarget(targetId: string, block: ScrollLogicalPosition): void {
  const target = document.getElementById(targetId);
  if (!target) return;

  const scrollableAncestor = getNearestScrollableAncestor(target);
  if (scrollableAncestor) {
    scrollElementWithinContainer(target, scrollableAncestor, block);
    return;
  }

  target.scrollIntoView({ behavior: "auto", block });
}

/**
 * Opens the Story Bible (toggle + left column + menu expansion when needed) and scrolls to the entity.
 * Used for # mention clicks from the composer and from rendered chat bubbles.
 */
export function navigateToEntityMention(id: string, options?: NavigateToEntityMentionOptions): void {
  const { setIsActive: setStoryBibleIsActive, collapsedSections, setSectionCollapsed } = useStoryBibleStore.getState();
  const { setDocumentId, project, document: activeDocument } = useProjectStore.getState();
  const setLeftBarCollapsed = useProjectStore.getState().setLeftBarCollapsed;
  markEntityNavigationInProgress();

  const jumpToTarget = (targetId: string, block: ScrollLogicalPosition, delayMs = 0, expandEntityId: string | null = null) => {
    const scrollToTarget = () => {
      scrollToEntityTarget(targetId, block);
    };

    const triggerNavigation = () => {
      if (expandEntityId) {
        window.dispatchEvent(
          new CustomEvent(EXPAND_STORY_BIBLE_ENTITY_EVENT, {
            detail: { id: expandEntityId },
          }),
        );
        requestAnimationFrame(() => {
          requestAnimationFrame(scrollToTarget);
        });
        return;
      }

      requestAnimationFrame(scrollToTarget);
    };

    if (delayMs > 0) {
      setTimeout(triggerNavigation, delayMs);
    } else {
      triggerNavigation();
    }
  };

  if (id.startsWith("character::")) {
    const plan = getEntityNavigationPlan(id, collapsedSections);
    setStoryBibleIsActive(true);
    setLeftBarCollapsed(false);
    if (plan.sectionToOpen) {
      setSectionCollapsed(plan.sectionToOpen, false);
    }
    window.dispatchEvent(new CustomEvent(EXPAND_STORY_BIBLE_MENU_EVENT));
    if (plan.targetId) {
      jumpToTarget(plan.targetId, plan.block, plan.delayMs, id);
    }
    return;
  }

  if (id.startsWith("worldbuilding::")) {
    const plan = getEntityNavigationPlan(id, collapsedSections);
    setStoryBibleIsActive(true);
    setLeftBarCollapsed(false);
    if (plan.sectionToOpen) {
      setSectionCollapsed(plan.sectionToOpen, false);
    }
    window.dispatchEvent(new CustomEvent(EXPAND_STORY_BIBLE_MENU_EVENT));
    if (plan.targetId) {
      jumpToTarget(plan.targetId, plan.block, plan.delayMs, id);
    }
    return;
  }

  if (id.startsWith("document::")) {
    setLeftBarCollapsed(false);
    setDocumentId(id.slice("document::".length));
    return;
  }

  if (id.startsWith("folder::")) {
    // Folder mentions currently act as context references; at minimum ensure the left sidebar is visible.
    setLeftBarCollapsed(false);
    return;
  }

  if (id.startsWith("series_document::")) {
    const parts = id.split("::");
    const targetProjectId = parts[1] ?? "";
    const targetDocumentId = parts[2] ?? "";
    if (!targetDocumentId) return;
    const currentProjectId = project?.id ?? null;
    const currentDocumentId = options?.activeDocumentId ?? activeDocument?.id ?? null;
    const differentSeriesBook = !!targetProjectId && !!currentProjectId && targetProjectId !== currentProjectId;

    // Cross-document series mentions open in a separate tab so users keep their current manuscript context.
    if (differentSeriesBook && currentDocumentId !== targetDocumentId) {
      const fallbackProjectId = project?.id ?? "";
      const projectIdForUrl = targetProjectId || fallbackProjectId;
      if (projectIdForUrl) {
        const url = `/app/project/${projectIdForUrl}#doc=${targetDocumentId}`;
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
    }

    setLeftBarCollapsed(false);
    setDocumentId(targetDocumentId);
    return;
  }

  if (id.startsWith("series::")) {
    const targetProjectId = id.slice("series::".length).trim();
    const currentProjectId = project?.id ?? "";
    if (targetProjectId && currentProjectId && targetProjectId !== currentProjectId) {
      const url = `/app/project/${targetProjectId}`;
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    setLeftBarCollapsed(false);
    return;
  }

  if (id.startsWith("story-bible-section::")) {
    const sectionKey = id.slice("story-bible-section::".length).trim();
    if (sectionKey) {
      navigateToStoryBibleFromChatLink(`sudowrite://story-bible/${sectionKey}`);
    }
    return;
  }

  if (id.startsWith("outline::")) {
    const chapterId = id.slice("outline::".length).trim();
    setStoryBibleIsActive(true);
    setLeftBarCollapsed(false);
    window.dispatchEvent(new CustomEvent(EXPAND_STORY_BIBLE_MENU_EVENT));
    const scrollTargetId = chapterId && chapterId !== "current"
      ? `story-bible-outline-chapter-${chapterId}`
      : "story-bible-outline";
    requestAnimationFrame(() => {
      const chapterTarget = chapterId && chapterId !== "current"
        ? document.getElementById(scrollTargetId)
        : null;
      if (chapterTarget) {
        scrollToEntityTarget(scrollTargetId, "start");
        return;
      }
      scrollToEntityTarget("story-bible-outline", "start");
    });
  }
}

/**
 * Handles `sudowrite://story-bible/...` links in agentic chat (mirrors comment deep links).
 * Returns whether the href was a recognized Story Bible link.
 */
export function navigateToStoryBibleFromChatLink(href: string): boolean {
  const parsed = parseAgenticChatStoryBibleLink(href);
  if (!parsed) {
    return false;
  }
  markEntityNavigationInProgress();

  if (parsed.kind === "character") {
    navigateToEntityMention(`character::${parsed.entityId}`);
    return true;
  }
  if (parsed.kind === "worldbuilding") {
    navigateToEntityMention(`worldbuilding::${parsed.entityId}`);
    return true;
  }

  const { setIsActive: setStoryBibleIsActive, collapsedSections, setSectionCollapsed } = useStoryBibleStore.getState();
  const setLeftBarCollapsed = useProjectStore.getState().setLeftBarCollapsed;
  const sectionKey = parsed.sectionKey;

  setStoryBibleIsActive(true);
  setLeftBarCollapsed(false);
  if (collapsedSections[sectionKey]) {
    setSectionCollapsed(sectionKey, false);
  }
  window.dispatchEvent(new CustomEvent(EXPAND_STORY_BIBLE_MENU_EVENT));
  requestAnimationFrame(() => {
    scrollToEntityTarget(`story-bible-${sectionKey}`, "start");
  });
  return true;
}
